import { createContext, use, useEffect, useState } from "react";
import themes from "devextreme/ui/themes";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FeatureFlag } from "@/constants";

type Theme = "dark" | "light" | "system";
type OutputTheme = "dark" | "light";

type ThemeProviderProps = {
    children: React.ReactNode;
    defaultTheme?: Theme;
    storageKey?: string;
};

type ThemeProviderState = {
    // Output theme is the theme with the system preference calculated
    outputTheme?: OutputTheme;
    // Theme is the selected theme
    theme: Theme;
    setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
    theme: "system",
    outputTheme: undefined,
    setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
    children,
    defaultTheme = "system",
    storageKey = "ui-theme",
    ...props
}: ThemeProviderProps) {
    const [theme, setTheme] = useState<Theme>(
        () => (localStorage.getItem(storageKey) as Theme) || defaultTheme,
    );
    const [outputTheme, setOutputTheme] = useState<"dark" | "light">();

    useEffect(() => {
        const root = window.document.body;

        // In Storybook, the theme toolbar addon owns the light/dark class on body —
        // observe it instead of setting it, so `outputTheme` stays in sync with the toolbar.
        if (import.meta.env["STORYBOOK"]) {
            const syncFromBody = () =>
                setOutputTheme(
                    root.classList.contains("dark") ? "dark" : "light",
                );
            syncFromBody();

            const observer = new MutationObserver(syncFromBody);
            observer.observe(root, {
                attributes: true,
                attributeFilter: ["class"],
            });
            return () => observer.disconnect();
        }

        root.classList.remove("light", "dark");

        if (theme === "system") {
            const systemTheme = window.matchMedia(
                "(prefers-color-scheme: dark)",
            ).matches
                ? "dark"
                : "light";
            setOutputTheme(systemTheme);
            root.classList.add(systemTheme);
            return;
        }
        setOutputTheme(theme);
        root.classList.add(theme);
    }, [theme]);

    useEffect(() => {
        // Don't run in test/storybook mode as it will blow up when it can't find the theme
        if (import.meta.env.MODE !== "test" && !import.meta.env["STORYBOOK"]) {
            themes.current(
                outputTheme === "light" ? "generic.light" : "generic.dark",
            );
        }
    }, [outputTheme]);

    const value = {
        theme,
        outputTheme,
        setTheme: (theme: Theme) => {
            localStorage.setItem(storageKey, theme);
            setTheme(theme);
        },
    };
    const flags = useFlags();

    useEffect(() => {
        const root = window.document.body;
        if (flags[FeatureFlag.IL_4194_POWER_MOVE]) {
            root.classList.add("power-move");
        } else {
            root.classList.remove("power-move");
        }
    }, [flags[FeatureFlag.IL_4194_POWER_MOVE]]);

    return (
        <ThemeProviderContext {...props} value={value}>
            {children}
        </ThemeProviderContext>
    );
}

export const useTheme = () => {
    const context = use(ThemeProviderContext);

    if (context === undefined)
        throw new Error("useTheme must be used within a ThemeProvider");

    return context;
};
